import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import RefreshIcon from '@mui/icons-material/Refresh';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';

import ClientDropdown from '@src/components/ClientDropdown';
import { useMessage } from '@src/context/common';
import emailMappingsApi from '@src/api/emailMappings';
import sftpMappingsApi from '@src/api/sftpMappings';
import apiMappingsApi from '@src/api/apiMappings';
import googleSheetMappingsApi from '@src/api/googleSheetMappings';

const generateId = () =>
  typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : Math.random().toString(36).slice(2);

const CATEGORY = {
  EMAIL: 'email',
  SFTP: 'sftp',
  API: 'api',
  GOOGLE_SHEET: 'google_sheet',
};

const CATEGORY_LABELS = {
  [CATEGORY.EMAIL]: 'Email',
  [CATEGORY.SFTP]: 'SFTP',
  [CATEGORY.API]: 'API',
  [CATEGORY.GOOGLE_SHEET]: 'Google Sheet',
};

const resolveCategoryFromParam = (value) => {
  if (
    value === CATEGORY.EMAIL
    || value === CATEGORY.SFTP
    || value === CATEGORY.API
    || value === CATEGORY.GOOGLE_SHEET
  ) {
    return value;
  }
  return CATEGORY.EMAIL;
};

const DEFAULT_FORM = {
  client: '',
  mapping_type: '',
  schedule_type: '',
  run_time: '',
  test_emails_text: '',
  include_amounts_in_signature: false,
  email_sender: '',
  cc: '',
  subject: '',
  email_body_contains: '',
  is_embedded_table: false,
  filename_substring: '',
  hostname: '',
  port: 22,
  username: '',
  password: '',
  directory: '',
  api_endpoint: '',
  api_key: '',
  json_headers_text: '',
  json_body_template_text: '',
  pagination_param: '',
  page_size_param: '',
  initial_page: 1,
  auth_url: '',
  auth_method: 'POST',
  auth_headers_text: '',
  auth_body_text: '',
  auth_token_path: 'token',
  auth_header_name: 'Authorization',
  auth_token_prefix: 'Bearer ',
  auth_query_param: '',
  spreadsheet_id: '',
  worksheet_range: '',
};

const CONDITION_OPERATORS = [
  { value: 'eq', label: 'Equals', needsValue: true },
  { value: 'contains', label: 'Contains', needsValue: true },
  { value: 'not_empty', label: 'Not empty', needsValue: false },
  { value: 'empty', label: 'Empty', needsValue: false },
  { value: 'is_blank', label: 'Is blank', needsValue: false },
  { value: 'gt_num', label: 'Greater than (number)', needsValue: true },
  { value: 'in', label: 'One of (comma separated)', needsValue: true },
];

const createColumnRow = () => ({
  id: generateId(),
  header: '',
  target: '',
});

const createValueMapRow = () => ({
  id: generateId(),
  match: '',
  result: '',
});

const createConditionRow = () => ({
  id: generateId(),
  field: '',
  operator: 'eq',
  value: '',
});

const createTransformRow = () => ({
  id: generateId(),
  setField: '',
  valueSource: 'field',
  fieldName: '',
  constantValue: '',
  defaultValue: '',
  valueMap: [],
  conditions: [],
});

const createFilterRow = () => ({
  id: generateId(),
  field: '',
  operator: 'eq',
  value: '',
});

const FILTER_OPERATORS = [
  { value: 'eq', label: 'Equals', needsValue: true },
  { value: 'in', label: 'One of (comma separated)', needsValue: true },
  { value: 'not_in', label: 'Not one of (comma separated)', needsValue: true },
];

const parseCsvHeaders = (text) => {
  if (!text) {
    return [];
  }
  const lines = text.split(/\r?\n/).filter((line) => line.trim().length);
  if (!lines.length) {
    return [];
  }
  const line = lines[0];
  const headers = [];
  let current = '';
  let inQuotes = false;

  for (let i = 0; i < line.length; i += 1) {
    const char = line[i];
    if (char === '"') {
      if (inQuotes && line[i + 1] === '"') {
        current += '"';
        i += 1;
      } else {
        inQuotes = !inQuotes;
      }
    } else if (char === ',' && !inQuotes) {
      headers.push(current.trim());
      current = '';
    } else {
      current += char;
    }
  }

  if (current) {
    headers.push(current.trim());
  }

  return headers.filter(Boolean);
};

const objectToColumnRows = (mapping) => {
  const rows = [];
  if (mapping && typeof mapping === 'object') {
    Object.entries(mapping).forEach(([header, target]) => {
      if (Array.isArray(target)) {
        target.forEach((value) => {
          rows.push({ id: generateId(), header, target: value });
        });
      } else {
        rows.push({ id: generateId(), header, target });
      }
    });
  }
  return rows.length ? rows : [createColumnRow()];
};

const parseTransformations = (params) => {
  const rows = [];
  const transformations = params?.transformations || [];
  if (!transformations.length) {
    return [createTransformRow()];
  }

  transformations.forEach((entry) => {
    const row = createTransformRow();
    row.setField = entry.set || '';
    if (entry.to && typeof entry.to === 'object' && !Array.isArray(entry.to)) {
      row.valueSource = 'field';
      row.fieldName = Array.isArray(entry.to.field) ? entry.to.field[0] : '';
      row.defaultValue = entry.to.default || '';
      const mapEntries = entry.to.value_map || {};
      row.valueMap = Object.entries(mapEntries).map(([match, result]) => ({
        id: generateId(),
        match,
        result,
      }));
    } else {
      row.valueSource = 'constant';
      row.constantValue = entry.to ?? '';
    }

    const conditions = entry.when?.all || [];
    row.conditions = conditions.map((condition) => {
      const operator = CONDITION_OPERATORS.find((item) =>
        Object.prototype.hasOwnProperty.call(condition, item.value)
      );
      return {
        id: generateId(),
        field: Array.isArray(condition.field) ? condition.field[0] : '',
        operator: operator?.value || 'eq',
        value:
          operator?.needsValue && condition[operator.value] !== undefined
            ? Array.isArray(condition[operator.value])
              ? condition[operator.value].join(', ')
              : String(condition[operator.value])
            : '',
      };
    });

    if (!row.conditions.length) {
      row.conditions = [];
    }
    rows.push(row);
  });

  return rows.length ? rows : [createTransformRow()];
};

const buildColumnMappingPayload = (rows) => {
  const payload = {};
  rows.forEach((row) => {
    if (!row.header || !row.target) {
      return;
    }
    if (payload[row.header]) {
      const existing = payload[row.header];
      if (Array.isArray(existing)) {
        if (!existing.includes(row.target)) {
          existing.push(row.target);
        }
      } else if (existing !== row.target) {
        payload[row.header] = [existing, row.target];
      }
    } else {
      payload[row.header] = row.target;
    }
  });
  return payload;
};

const buildConditionPayload = (condition) => {
  if (!condition.field) {
    return null;
  }
  const base = { field: [condition.field] };
  switch (condition.operator) {
    case 'eq':
      if (!condition.value) {
        return null;
      }
      base.eq = condition.value;
      break;
    case 'contains':
      if (!condition.value) {
        return null;
      }
      base.contains = condition.value;
      break;
    case 'not_empty':
      base.not_empty = true;
      break;
    case 'empty':
      base.empty = true;
      break;
    case 'is_blank':
      base.is_blank = true;
      break;
    case 'gt_num': {
      const num = Number(condition.value);
      if (Number.isNaN(num)) {
        return null;
      }
      base.gt_num = num;
      break;
    }
    case 'in': {
      if (!condition.value) {
        return null;
      }
      const values = condition.value
        .split(',')
        .map((item) => item.trim())
        .filter(Boolean);
      if (!values.length) {
        return null;
      }
      base.in = values;
      break;
    }
    default:
      return null;
  }
  return base;
};

const buildTransformPayload = (rows) => {
  const payload = rows
    .map((row) => {
      if (!row.setField) {
        return null;
      }
      const entry = { set: row.setField };

      if (row.valueSource === 'constant') {
        if (!row.constantValue) {
          return null;
        }
        entry.to = row.constantValue;
      } else {
        if (!row.fieldName) {
          return null;
        }
        const to = { field: [row.fieldName] };
        if (row.valueMap?.length) {
          const valueMap = {};
          row.valueMap.forEach((item) => {
            if (item.match) {
              valueMap[item.match] = item.result || '';
            }
          });
          if (Object.keys(valueMap).length) {
            to.value_map = valueMap;
          }
        }
        if (row.defaultValue) {
          to.default = row.defaultValue;
        }
        entry.to = to;
      }

      const conditionPayloads =
        row.conditions
          ?.map((condition) => buildConditionPayload(condition))
          .filter(Boolean) || [];
      if (conditionPayloads.length) {
        entry.when = { all: conditionPayloads };
      }
      return entry;
    })
    .filter(Boolean);

  return payload.length ? { transformations: payload } : {};
};

const parseFilterRows = (params) => {
  const filters = params?.filter_rows || [];
  if (!Array.isArray(filters) || !filters.length) {
    return [createFilterRow()];
  }
  return filters.map((condition) => {
    const operator =
      Object.keys(condition || {}).find((key) =>
        FILTER_OPERATORS.some((item) => item.value === key)
      ) || 'eq';
    const rawValue = condition?.[operator];
    const value = Array.isArray(rawValue)
      ? rawValue.join(', ')
      : rawValue !== undefined && rawValue !== null
        ? String(rawValue)
        : '';
    return {
      id: generateId(),
      field: Array.isArray(condition?.field)
        ? condition.field[0]
        : condition?.field || '',
      operator,
      value,
    };
  });
};

const buildFilterRowsPayload = (rows) => {
  if (!Array.isArray(rows) || !rows.length) {
    return {};
  }
  const payload = rows
    .map((row) => {
      if (!row.field || !row.operator) {
        return null;
      }
      const base = { field: row.field };
      const trimmedValue = typeof row.value === 'string' ? row.value.trim() : '';
      if (row.operator === 'eq') {
        if (!trimmedValue) {
          return null;
        }
        base.eq = trimmedValue;
      } else if (row.operator === 'in' || row.operator === 'not_in') {
        const values = trimmedValue
          ? trimmedValue
              .split(',')
              .map((item) => item.trim())
              .filter(Boolean)
          : [];
        if (!values.length) {
          return null;
        }
        base[row.operator] = values;
      } else {
        return null;
      }
      return base;
    })
    .filter(Boolean);

  return payload.length ? { filter_rows: payload } : {};
};

export default function EmailMappingBuilderPage() {
  const [searchParams] = useSearchParams();
  const initialCategory = resolveCategoryFromParam(searchParams.get('category'));
  const mappingClientFilter = searchParams.get('client_id') || '';
  const message = useMessage();
  const [metadata, setMetadata] = useState({
    backend_fields: [],
    mapping_types: [],
    schedule_types: [],
  });
  const [activeCategory, setActiveCategory] = useState(initialCategory);
  const [emailMappings, setEmailMappings] = useState([]);
  const [sftpMappings, setSftpMappings] = useState([]);
  const [apiMappings, setApiMappings] = useState([]);
  const [googleSheetMappings, setGoogleSheetMappings] = useState([]);
  const [listLoading, setListLoading] = useState({
    email: false,
    sftp: false,
    api: false,
    google_sheet: false,
  });
  const [activeEmailId, setActiveEmailId] = useState(null);
  const [activeSftpId, setActiveSftpId] = useState(null);
  const [activeApiId, setActiveApiId] = useState(null);
  const [activeGoogleSheetId, setActiveGoogleSheetId] = useState(null);
  const [formValues, setFormValues] = useState(DEFAULT_FORM);
  const [columnRows, setColumnRows] = useState([createColumnRow()]);
  const [transformRows, setTransformRows] = useState([createTransformRow()]);
  const [filterRows, setFilterRows] = useState([createFilterRow()]);
  const [headerSuggestions, setHeaderSuggestions] = useState([]);
  const [saving, setSaving] = useState(false);
  const [runLoading, setRunLoading] = useState(false);
  const activeId =
    activeCategory === CATEGORY.EMAIL
      ? activeEmailId
      : activeCategory === CATEGORY.SFTP
        ? activeSftpId
        : activeCategory === CATEGORY.API
          ? activeApiId
          : activeGoogleSheetId;
  const currentMappings =
    activeCategory === CATEGORY.EMAIL
      ? emailMappings
      : activeCategory === CATEGORY.SFTP
        ? sftpMappings
        : activeCategory === CATEGORY.API
          ? apiMappings
          : googleSheetMappings;
  const isListLoading = listLoading[activeCategory];

  const resolveMappingType = (category = activeCategory, mappingTypes = metadata.mapping_types) => {
    const options = mappingTypes || [];
    const match = options.find((option) => option.value === category);
    if (match) {
      return match.value;
    }
    return (
      options[0]?.value ||
      (category === CATEGORY.SFTP
        ? 'sftp'
        : category === CATEGORY.API
          ? 'api'
          : category === CATEGORY.GOOGLE_SHEET
            ? 'google_sheet'
            : 'email')
    );
  };

  const buildDefaultForm = useCallback(
    (category = activeCategory) => ({
      ...DEFAULT_FORM,
      client: mappingClientFilter || '',
      mapping_type: resolveMappingType(category),
      schedule_type:
        metadata.schedule_types?.[0]?.value || 'daily',
    }),
    [metadata.schedule_types, activeCategory, mappingClientFilter]
  );

  const loadMetadata = useCallback(async () => {
    try {
      const response = await emailMappingsApi.metadata();
      const metadataPayload = response.data || {};
      const preferredMappingType = resolveMappingType(
        activeCategory,
        metadataPayload.mapping_types
      );
      setMetadata(metadataPayload);
      setFormValues((prev) => ({
        ...prev,
        mapping_type: prev.mapping_type || preferredMappingType,
        schedule_type:
          metadataPayload.schedule_types?.[0]?.value ||
          prev.schedule_type ||
          'daily',
      }));
    } catch (error) {
      console.error('Failed to load metadata', error);
      message.error('Unable to load mapping metadata.');
    }
  }, [message, activeCategory]);

  const loadEmailMappings = useCallback(async () => {
    setListLoading((prev) => ({ ...prev, [CATEGORY.EMAIL]: true }));
    try {
      const response = await emailMappingsApi.list();
      const results = Array.isArray(response.data?.results)
        ? response.data.results
        : response.data;
      setEmailMappings(Array.isArray(results) ? results : []);
    } catch (error) {
      console.error('Failed to load mappings', error);
      message.error('Unable to load email mappings.');
    } finally {
      setListLoading((prev) => ({ ...prev, [CATEGORY.EMAIL]: false }));
    }
  }, [message]);

  const loadSftpMappings = useCallback(async () => {
    setListLoading((prev) => ({ ...prev, [CATEGORY.SFTP]: true }));
    try {
      const response = await sftpMappingsApi.list();
      const results = Array.isArray(response.data?.results)
        ? response.data.results
        : response.data;
      setSftpMappings(Array.isArray(results) ? results : []);
    } catch (error) {
      console.error('Failed to load SFTP mappings', error);
      message.error('Unable to load SFTP mappings.');
    } finally {
      setListLoading((prev) => ({ ...prev, [CATEGORY.SFTP]: false }));
    }
  }, [message]);

  const loadApiMappings = useCallback(async () => {
    setListLoading((prev) => ({ ...prev, [CATEGORY.API]: true }));
    try {
      const response = await apiMappingsApi.list();
      const results = Array.isArray(response.data?.results)
        ? response.data.results
        : response.data;
      setApiMappings(Array.isArray(results) ? results : []);
    } catch (error) {
      console.error('Failed to load API mappings', error);
      message.error('Unable to load API mappings.');
    } finally {
      setListLoading((prev) => ({ ...prev, [CATEGORY.API]: false }));
    }
  }, [message]);

  const loadGoogleSheetMappings = useCallback(async () => {
    setListLoading((prev) => ({ ...prev, [CATEGORY.GOOGLE_SHEET]: true }));
    try {
      const response = await googleSheetMappingsApi.list();
      const results = Array.isArray(response.data?.results)
        ? response.data.results
        : response.data;
      setGoogleSheetMappings(Array.isArray(results) ? results : []);
    } catch (error) {
      console.error('Failed to load Google Sheet mappings', error);
      message.error('Unable to load Google Sheet mappings.');
    } finally {
      setListLoading((prev) => ({
        ...prev,
        [CATEGORY.GOOGLE_SHEET]: false,
      }));
    }
  }, [message]);

  useEffect(() => {
    loadMetadata();
    loadEmailMappings();
    loadSftpMappings();
    loadApiMappings();
    loadGoogleSheetMappings();
    // Dependencies intentionally omitted to avoid repeated fetching loops
    // in StrictMode/dev when callbacks get new identities.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!mappingClientFilter || activeId) {
      return;
    }
    setFormValues((prev) =>
      prev.client ? prev : { ...prev, client: mappingClientFilter }
    );
  }, [mappingClientFilter, activeId]);

  const resetForm = (category = activeCategory) => {
    if (category === CATEGORY.EMAIL) {
      setActiveEmailId(null);
    } else if (category === CATEGORY.SFTP) {
      setActiveSftpId(null);
    } else if (category === CATEGORY.API) {
      setActiveApiId(null);
    } else {
      setActiveGoogleSheetId(null);
    }
    setFormValues(buildDefaultForm(category));
    setColumnRows([createColumnRow()]);
    setTransformRows([createTransformRow()]);
    setFilterRows([createFilterRow()]);
    setHeaderSuggestions([]);
  };

  const buildFormFromMapping = (mapping, category = activeCategory) => {
    const baseForm = buildDefaultForm(category);
    const includeAmountsInSignature = Boolean(
      mapping?.transform_params?.dedupe?.include_amounts_in_signature ||
        mapping?.transform_params?.include_amounts_in_signature
    );
    const nextForm = {
      ...baseForm,
      client: mapping?.client || '',
      mapping_type: mapping?.mapping_type || baseForm.mapping_type,
      schedule_type: mapping?.schedule_type || baseForm.schedule_type,
      run_time: mapping?.run_time || '',
      test_emails_text: (mapping?.test_emails || []).join(', '),
      include_amounts_in_signature: includeAmountsInSignature,
      filename_substring:
        category === CATEGORY.EMAIL || category === CATEGORY.SFTP
          ? mapping?.filename_substring || ''
          : '',
    };

    if (category === CATEGORY.EMAIL) {
      Object.assign(nextForm, {
        email_sender: mapping?.email_sender || '',
        cc: mapping?.cc || '',
        subject: mapping?.subject || '',
        email_body_contains: mapping?.email_body_contains || '',
        is_embedded_table: Boolean(mapping?.is_embedded_table),
      });
    } else if (category === CATEGORY.SFTP) {
      Object.assign(nextForm, {
        hostname: mapping?.hostname || '',
        port:
          typeof mapping?.port === 'number' ? mapping.port : DEFAULT_FORM.port,
        username: mapping?.username || '',
        password: mapping?.password || '',
        directory: mapping?.directory || '',
      });
    } else if (category === CATEGORY.API) {
      Object.assign(nextForm, {
        api_endpoint: mapping?.api_endpoint || '',
        api_key: mapping?.api_key || '',
        json_headers_text: mapping?.json_headers
          ? JSON.stringify(mapping.json_headers, null, 2)
          : '',
        json_body_template_text: mapping?.json_body_template
          ? JSON.stringify(mapping.json_body_template, null, 2)
          : '',
        pagination_param: mapping?.pagination_param || '',
        page_size_param: mapping?.page_size_param || '',
        initial_page:
          typeof mapping?.initial_page === 'number'
            ? mapping.initial_page
            : DEFAULT_FORM.initial_page,
        auth_url: mapping?.transform_params?.auth?.url || '',
        auth_method: mapping?.transform_params?.auth?.method || DEFAULT_FORM.auth_method,
        auth_headers_text: mapping?.transform_params?.auth?.headers
          ? JSON.stringify(mapping.transform_params.auth.headers, null, 2)
          : '',
        auth_body_text: mapping?.transform_params?.auth?.body
          ? JSON.stringify(mapping.transform_params.auth.body, null, 2)
          : '',
        auth_token_path: mapping?.transform_params?.auth?.token_path || DEFAULT_FORM.auth_token_path,
        auth_header_name: mapping?.transform_params?.auth?.header_name || DEFAULT_FORM.auth_header_name,
        auth_token_prefix: mapping?.transform_params?.auth?.token_prefix ?? DEFAULT_FORM.auth_token_prefix,
        auth_query_param: mapping?.transform_params?.auth?.query_param || '',
      });
    } else {
      Object.assign(nextForm, {
        spreadsheet_id: mapping?.spreadsheet_id || '',
        worksheet_range: mapping?.worksheet_range || '',
      });
    }

    return nextForm;
  };

  const handleSelectMapping = (mapping, category = activeCategory) => {
    if (!mapping) {
      resetForm(category);
      return;
    }
    if (category === CATEGORY.EMAIL) {
      setActiveEmailId(mapping.id);
    } else if (category === CATEGORY.SFTP) {
      setActiveSftpId(mapping.id);
    } else if (category === CATEGORY.API) {
      setActiveApiId(mapping.id);
    } else {
      setActiveGoogleSheetId(mapping.id);
    }

    setFormValues(buildFormFromMapping(mapping, category));
    setColumnRows(objectToColumnRows(mapping.column_mappings));
    setTransformRows(parseTransformations(mapping.transform_params));
    setFilterRows(parseFilterRows(mapping.transform_params));
    setHeaderSuggestions(Object.keys(mapping.column_mappings || {}));
  };

  const handleFieldChange = (field, value) => {
    setFormValues((prev) => ({ ...prev, [field]: value }));
  };

  const handleColumnChange = (rowId, field, value) => {
    setColumnRows((prev) =>
      prev.map((row) =>
        row.id === rowId ? { ...row, [field]: value } : row
      )
    );
  };

  const handleTransformChange = (rowId, field, value) => {
    setTransformRows((prev) =>
      prev.map((row) =>
        row.id === rowId ? { ...row, [field]: value } : row
      )
    );
  };

  const handleConditionChange = (rowId, conditionId, field, value) => {
    setTransformRows((prev) =>
      prev.map((row) => {
        if (row.id !== rowId) {
          return row;
        }
        const conditions = row.conditions?.length ? row.conditions : [];
        return {
          ...row,
          conditions: conditions.map((condition) =>
            condition.id === conditionId
              ? { ...condition, [field]: value }
              : condition
          ),
        };
      })
    );
  };

  const handleValueMapChange = (rowId, mapId, field, value) => {
    setTransformRows((prev) =>
      prev.map((row) => {
        if (row.id !== rowId) {
          return row;
        }
        const valueMap = row.valueMap?.length ? row.valueMap : [];
        return {
          ...row,
          valueMap: valueMap.map((entry) =>
            entry.id === mapId ? { ...entry, [field]: value } : entry
          ),
        };
      })
    );
  };

  const addCondition = (rowId) => {
    setTransformRows((prev) =>
      prev.map((row) =>
        row.id === rowId
          ? {
              ...row,
              conditions: [...(row.conditions || []), createConditionRow()],
            }
          : row
      )
    );
  };

  const removeCondition = (rowId, conditionId) => {
    setTransformRows((prev) =>
      prev.map((row) =>
        row.id === rowId
          ? {
              ...row,
              conditions: (row.conditions || []).filter(
                (condition) => condition.id !== conditionId
              ),
            }
          : row
      )
    );
  };

  const addValueMap = (rowId) => {
    setTransformRows((prev) =>
      prev.map((row) =>
        row.id === rowId
          ? {
              ...row,
              valueMap: [...(row.valueMap || []), createValueMapRow()],
            }
          : row
      )
    );
  };

  const removeValueMap = (rowId, mapId) => {
    setTransformRows((prev) =>
      prev.map((row) =>
        row.id === rowId
          ? {
              ...row,
              valueMap: (row.valueMap || []).filter(
                (entry) => entry.id !== mapId
              ),
            }
          : row
      )
    );
  };

  const handleFilterChange = (rowId, field, value) => {
    setFilterRows((prev) =>
      prev.map((row) => (row.id === rowId ? { ...row, [field]: value } : row))
    );
  };

  const addFilterRow = () => {
    setFilterRows((prev) => [...prev, createFilterRow()]);
  };

  const removeFilterRow = (rowId) => {
    setFilterRows((prev) =>
      prev.length > 1 ? prev.filter((row) => row.id !== rowId) : prev
    );
  };

  const handleCsvUpload = (event) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const headers = parseCsvHeaders(reader.result);
      if (!headers.length) {
        message.error('Could not detect column headers.');
        return;
      }
      setHeaderSuggestions((prev) => {
        const merged = new Set([...(prev || []), ...headers]);
        return Array.from(merged);
      });
      message.success('Detected CSV headers.');
    };
    reader.readAsText(file);
    event.target.value = '';
  };

  const handleSave = async () => {
    const isEmail = activeCategory === CATEGORY.EMAIL;
    const isSftp = activeCategory === CATEGORY.SFTP;
    const isApi = activeCategory === CATEGORY.API;
    const isGoogle = activeCategory === CATEGORY.GOOGLE_SHEET;
    const mappingTypeFallback = resolveMappingType(activeCategory);
    const parseJsonField = (text, fieldLabel) => {
      if (!text || !text.trim()) {
        return {};
      }
      try {
        const parsed = JSON.parse(text);
        if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
          throw new Error('Not an object');
        }
        return parsed;
      } catch (error) {
        console.error(`Failed to parse ${fieldLabel}`, error);
        message.error(`${fieldLabel} must be valid JSON.`);
        throw error;
      }
    };

    const buildAuthPayload = () => {
      if (activeCategory !== CATEGORY.API) {
        return {};
      }
      const hasAny =
        formValues.auth_url ||
        formValues.auth_headers_text?.trim() ||
        formValues.auth_body_text?.trim() ||
        formValues.auth_header_name ||
        formValues.auth_query_param;

      if (!hasAny) {
        return {};
      }

      let parsedHeaders = {};
      let parsedBody = {};
      try {
        parsedHeaders = parseJsonField(
          formValues.auth_headers_text,
          'Auth headers'
        );
        parsedBody = parseJsonField(formValues.auth_body_text, 'Auth body');
      } catch (error) {
        throw error;
      }

      return {
        url: formValues.auth_url,
        method: (formValues.auth_method || 'POST').toUpperCase(),
        headers: parsedHeaders,
        body: parsedBody,
        token_path: formValues.auth_token_path || 'token',
        header_name: formValues.auth_header_name || '',
        token_prefix: formValues.auth_token_prefix ?? '',
        query_param: formValues.auth_query_param || '',
      };
    };

    const payload = {
      client: formValues.client ? Number(formValues.client) : null,
      mapping_type:
        formValues.mapping_type || mappingTypeFallback,
      schedule_type:
        formValues.schedule_type ||
        metadata.schedule_types?.[0]?.value ||
        'daily',
      run_time: formValues.run_time || null,
      test_emails: formValues.test_emails_text
        ? formValues.test_emails_text
            .split(',')
            .map((email) => email.trim())
            .filter(Boolean)
        : [],
      column_mappings: buildColumnMappingPayload(columnRows),
      transform_params: (() => {
        const base = {
          ...(isApi ? {} : buildFilterRowsPayload(filterRows)),
          ...buildTransformPayload(transformRows),
        };
        if (formValues.include_amounts_in_signature) {
          base.dedupe = {
            ...(base.dedupe || {}),
            include_amounts_in_signature: true,
          };
        }
        if (isApi) {
          const auth = buildAuthPayload();
          if (Object.keys(auth).length) {
            base.auth = auth;
          }
        }
        return base;
      })(),
    };

    if (isEmail) {
      Object.assign(payload, {
        email_sender: formValues.email_sender || '',
        cc: formValues.cc || '',
        subject: formValues.subject || '',
        email_body_contains: formValues.email_body_contains || '',
        is_embedded_table: Boolean(formValues.is_embedded_table),
        filename_substring: formValues.filename_substring || '',
      });
    } else if (isSftp) {
      Object.assign(payload, {
        hostname: formValues.hostname || '',
        port: Number(formValues.port) || DEFAULT_FORM.port,
        username: formValues.username || '',
        password: formValues.password || '',
        directory: formValues.directory || '',
        filename_substring: formValues.filename_substring || '',
      });
    } else if (isApi) {
      let parsedHeaders = {};
      let parsedBody = {};
      try {
        parsedHeaders = parseJsonField(
          formValues.json_headers_text,
          'Headers'
        );
        parsedBody = parseJsonField(
          formValues.json_body_template_text,
          'Body template'
        );
      } catch (error) {
        return;
      }
      Object.assign(payload, {
        api_endpoint: formValues.api_endpoint || '',
        api_key: formValues.api_key || '',
        json_headers: parsedHeaders,
        json_body_template: parsedBody,
        pagination_param: formValues.pagination_param || '',
        page_size_param: formValues.page_size_param || '',
        initial_page:
          formValues.initial_page !== '' &&
          !Number.isNaN(Number(formValues.initial_page))
            ? Number(formValues.initial_page)
            : DEFAULT_FORM.initial_page,
      });
    } else if (isGoogle) {
      Object.assign(payload, {
        spreadsheet_id: formValues.spreadsheet_id || '',
        worksheet_range: formValues.worksheet_range || '',
      });
    }

    if (!payload.client) {
      message.error('Please select a client for this mapping.');
      return;
    }
    if (isEmail && !payload.subject) {
      message.error('Please enter a subject to identify the mapping.');
      return;
    }
    if (isSftp) {
      if (!payload.hostname || !payload.username || !payload.directory) {
        message.error('Hostname, username, and directory are required.');
        return;
      }
    }
    if (isApi && !payload.api_endpoint) {
      message.error('Please enter an API endpoint URL.');
      return;
    }
    if (isGoogle) {
      if (!payload.spreadsheet_id || !payload.worksheet_range) {
        message.error('Spreadsheet ID and worksheet range are required.');
        return;
      }
    }

    const api = isEmail
      ? emailMappingsApi
      : isSftp
        ? sftpMappingsApi
        : isApi
          ? apiMappingsApi
          : googleSheetMappingsApi;
    setSaving(true);
    try {
      let newId = activeId;
      if (activeId) {
        await api.update(activeId, payload);
        message.success(
          `${CATEGORY_LABELS[activeCategory]} mapping updated.`
        );
      } else {
        const response = await api.create(payload);
        newId = response.data?.id || null;
        if (isEmail) {
          setActiveEmailId(newId);
        } else if (isSftp) {
          setActiveSftpId(newId);
        } else if (isApi) {
          setActiveApiId(newId);
        } else {
          setActiveGoogleSheetId(newId);
        }
        message.success(
          `${CATEGORY_LABELS[activeCategory]} mapping created.`
        );
      }
      const reloadFn = isEmail
        ? loadEmailMappings
        : isSftp
          ? loadSftpMappings
          : isApi
            ? loadApiMappings
            : loadGoogleSheetMappings;
      await reloadFn();
      if (!activeId && newId) {
        setFormValues((prev) => ({ ...prev }));
      }
    } catch (error) {
      console.error('Failed to save mapping', error);
      message.error('Unable to save mapping.');
    } finally {
      setSaving(false);
    }
  };

  const handleRunNow = async () => {
    if (!activeId) {
      message.error('Select a mapping to run.');
      return;
    }
    const isEmail = activeCategory === CATEGORY.EMAIL;
    const isSftp = activeCategory === CATEGORY.SFTP;
    const isApi = activeCategory === CATEGORY.API;
    const isGoogle = activeCategory === CATEGORY.GOOGLE_SHEET;
    const apiClient = isEmail
      ? emailMappingsApi
      : isSftp
        ? sftpMappingsApi
        : isApi
          ? apiMappingsApi
          : googleSheetMappingsApi;
    setRunLoading(true);
    try {
      await apiClient.run(activeId);
      const successMessage = isApi
        ? 'API mapping run acknowledged.'
        : `${CATEGORY_LABELS[activeCategory]} mapping processing has been queued.`;
      message.success(successMessage);
    } catch (error) {
      console.error('Failed to trigger mapping run', error);
      message.error('Unable to start mapping processing.');
    } finally {
      setRunLoading(false);
    }
  };

  const handleCategoryChange = (_, value) => {
    if (!value || value === activeCategory) {
      return;
    }
    setActiveCategory(value);
    const targetId =
      value === CATEGORY.EMAIL
        ? activeEmailId
        : value === CATEGORY.SFTP
          ? activeSftpId
          : value === CATEGORY.API
            ? activeApiId
            : activeGoogleSheetId;
    const targetList =
      value === CATEGORY.EMAIL
        ? emailMappings
        : value === CATEGORY.SFTP
          ? sftpMappings
          : value === CATEGORY.API
            ? apiMappings
            : googleSheetMappings;
    if (targetId) {
      const target = targetList.find((item) => item.id === targetId);
      if (target) {
        handleSelectMapping(target, value);
        return;
      }
    }
    resetForm(value);
  };

  const usedColumnTargets = useMemo(() => {
    const used = new Set();
    columnRows.forEach((row) => {
      if (row.target) {
        used.add(row.target);
      }
    });
    return used;
  }, [columnRows]);

  const mappingList = useMemo(
    () =>
      (Array.isArray(currentMappings) ? currentMappings : [])
        .filter((mapping) => {
          if (!mappingClientFilter) {
            return true;
          }
          return String(mapping?.client || '') === String(mappingClientFilter);
        })
        .map((mapping) => {
          const label =
            mapping.subject ||
            mapping.directory ||
            mapping.hostname ||
            mapping.api_endpoint ||
            mapping.worksheet_range ||
            mapping.spreadsheet_id ||
            'Untitled';
          return {
            ...mapping,
            displayName: mapping.client_name
              ? `${mapping.client_name} · ${label}`
              : label,
          };
        })
        .sort((a, b) => a.displayName.localeCompare(b.displayName)),
    [currentMappings, mappingClientFilter]
  );

  const duplicateMapping = () => {
    if (!activeId) {
      message.error('Select a mapping to duplicate.');
      return;
    }
    const mappingListSource =
      activeCategory === CATEGORY.EMAIL
        ? emailMappings
        : activeCategory === CATEGORY.SFTP
          ? sftpMappings
          : activeCategory === CATEGORY.API
            ? apiMappings
            : googleSheetMappings;
    const mapping = (Array.isArray(mappingListSource)
      ? mappingListSource
      : []
    ).find((item) => item.id === activeId);
    if (!mapping) {
      message.error('Could not find the selected mapping to duplicate.');
      return;
    }

    setFormValues(buildFormFromMapping(mapping, activeCategory));
    setColumnRows(objectToColumnRows(mapping.column_mappings));
    setTransformRows(parseTransformations(mapping.transform_params));
    setFilterRows(parseFilterRows(mapping.transform_params));
    setHeaderSuggestions(Object.keys(mapping.column_mappings || {}));
    setActiveEmailId(null);
    setActiveSftpId(null);
    setActiveApiId(null);
    setActiveGoogleSheetId(null);
    message.success('Mapping duplicated. Review and save as a new record.');
  };

  const isEmailCategory = activeCategory === CATEGORY.EMAIL;
  const isSftpCategory = activeCategory === CATEGORY.SFTP;
  const isApiCategory = activeCategory === CATEGORY.API;
  const isGoogleCategory = activeCategory === CATEGORY.GOOGLE_SHEET;
  const categoryLabel = CATEGORY_LABELS[activeCategory];
  const refreshHandler =
    activeCategory === CATEGORY.EMAIL
      ? loadEmailMappings
      : isSftpCategory
        ? loadSftpMappings
        : isApiCategory
          ? loadApiMappings
          : loadGoogleSheetMappings;

  return (
    <Box p={3}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        justifyContent="space-between"
        spacing={2}
        mb={3}
      >
        <Typography variant="h4">Mapping builder</Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems="center">
          <Button
            variant="outlined"
            size="small"
            onClick={duplicateMapping}
            disabled={!activeId}
          >
            Duplicate
          </Button>
          <ToggleButtonGroup
            color="primary"
            exclusive
            value={activeCategory}
            onChange={handleCategoryChange}
            size="small"
          >
            <ToggleButton value={CATEGORY.EMAIL}>Email mappings</ToggleButton>
            <ToggleButton value={CATEGORY.SFTP}>SFTP mappings</ToggleButton>
            <ToggleButton value={CATEGORY.API}>API mappings</ToggleButton>
            <ToggleButton value={CATEGORY.GOOGLE_SHEET}>
              Google Sheet mappings
            </ToggleButton>
          </ToggleButtonGroup>
        </Stack>
      </Stack>
      <Grid container spacing={3}>
        <Grid item xs={12} md={3}>
          <Stack spacing={2}>
            <Card>
              <CardHeader
                title={`${categoryLabel} mappings`}
                action={
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<RefreshIcon />}
                    onClick={refreshHandler}
                  >
                    Refresh
                  </Button>
                }
              />
              <Divider />
              <CardContent sx={{ p: 0 }}>
                {isListLoading ? (
                  <Stack
                    alignItems="center"
                    justifyContent="center"
                    sx={{ py: 4 }}
                  >
                    <CircularProgress size={32} />
                  </Stack>
                ) : (
                  <List disablePadding>
                    <ListItemButton
                      selected={!activeId}
                      onClick={() => resetForm(activeCategory)}
                    >
                      <ListItemText
                        primary="+ Create new mapping"
                        primaryTypographyProps={{ fontWeight: 600 }}
                      />
                    </ListItemButton>
                    {mappingList.map((mapping) => (
                      <ListItemButton
                        key={mapping.id}
                        selected={activeId === mapping.id}
                        onClick={() => handleSelectMapping(mapping)}
                      >
                        <ListItemText
                          primary={mapping.displayName}
                          secondary={mapping.mapping_type?.toUpperCase()}
                        />
                      </ListItemButton>
                    ))}
                    {!mappingList.length && (
                      <Box p={3}>
                        <Typography variant="body2" color="text.secondary">
                          No mappings yet. Create your first mapping to get
                          started.
                        </Typography>
                      </Box>
                    )}
                  </List>
                )}
              </CardContent>
            </Card>
          </Stack>
        </Grid>
        <Grid item xs={12} md={9}>
          <Stack spacing={3}>
            <Card>
              <CardHeader title="General settings" />
              <Divider />
              <CardContent>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <ClientDropdown
                      value={formValues.client}
                      onChange={(value) => handleFieldChange('client', value)}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      select
                      fullWidth
                      label="Mapping type"
                      value={formValues.mapping_type}
                      onChange={(event) =>
                        handleFieldChange('mapping_type', event.target.value)
                      }
                    >
                      {(metadata.mapping_types || []).map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      select
                      fullWidth
                      label="Schedule type"
                      value={formValues.schedule_type}
                      onChange={(event) =>
                        handleFieldChange('schedule_type', event.target.value)
                      }
                    >
                      {(metadata.schedule_types || []).map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      type="time"
                      label="Run time"
                      InputLabelProps={{ shrink: true }}
                      value={formValues.run_time || ''}
                      onChange={(event) =>
                        handleFieldChange('run_time', event.target.value)
                      }
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Test emails"
                      helperText="Comma separated email addresses that should receive verbose logs."
                      value={formValues.test_emails_text}
                      onChange={(event) =>
                        handleFieldChange(
                          'test_emails_text',
                          event.target.value
                        )
                      }
                    />
                  </Grid>
                  <Grid
                    item
                    xs={12}
                    sm={6}
                    display="flex"
                    alignItems="center"
                  >
                    <FormControlLabel
                      control={
                        <Switch
                          checked={Boolean(
                            formValues.include_amounts_in_signature
                          )}
                          onChange={(event) =>
                            handleFieldChange(
                              'include_amounts_in_signature',
                              event.target.checked
                            )
                          }
                        />
                      }
                      label="Treat amount variants as distinct records"
                    />
                  </Grid>
                  {isGoogleCategory && (
                    <>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Spreadsheet ID or URL"
                          value={formValues.spreadsheet_id}
                          onChange={(event) =>
                            handleFieldChange(
                              'spreadsheet_id',
                              event.target.value
                            )
                          }
                          helperText="Paste the Google Sheet ID or full URL."
                          required
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Worksheet range"
                          value={formValues.worksheet_range}
                          onChange={(event) =>
                            handleFieldChange(
                              'worksheet_range',
                              event.target.value
                            )
                          }
                          helperText='Include the tab name, e.g., "Sheet1!A:Z".'
                          required
                        />
                      </Grid>
                    </>
                  )}
                  {isEmailCategory && (
                    <>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Email sender"
                          value={formValues.email_sender}
                          onChange={(event) =>
                            handleFieldChange('email_sender', event.target.value)
                          }
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="CC email"
                          value={formValues.cc}
                          onChange={(event) =>
                            handleFieldChange('cc', event.target.value)
                          }
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Email subject"
                          value={formValues.subject}
                          onChange={(event) =>
                            handleFieldChange('subject', event.target.value)
                          }
                          required
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          multiline
                          minRows={2}
                          label="Email body contains"
                          value={formValues.email_body_contains}
                          onChange={(event) =>
                            handleFieldChange(
                              'email_body_contains',
                              event.target.value
                            )
                          }
                        />
                      </Grid>
                      <Grid
                        item
                        xs={12}
                        sm={6}
                        display="flex"
                        alignItems="center"
                      >
                        <FormControlLabel
                          control={
                            <Switch
                              checked={formValues.is_embedded_table}
                              onChange={(event) =>
                                handleFieldChange(
                                  'is_embedded_table',
                                  event.target.checked
                                )
                              }
                            />
                          }
                          label="Attachment contains embedded table"
                        />
                      </Grid>
                    </>
                  )}
                  {isSftpCategory && (
                    <>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Hostname"
                          value={formValues.hostname}
                          onChange={(event) =>
                            handleFieldChange('hostname', event.target.value)
                          }
                          required
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Port"
                          type="number"
                          value={formValues.port}
                          onChange={(event) =>
                            handleFieldChange('port', event.target.value)
                          }
                          required
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Username"
                          value={formValues.username}
                          onChange={(event) =>
                            handleFieldChange('username', event.target.value)
                          }
                          required
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          type="password"
                          label="Password"
                          value={formValues.password}
                          onChange={(event) =>
                            handleFieldChange('password', event.target.value)
                          }
                          required
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Directory"
                          value={formValues.directory}
                          onChange={(event) =>
                            handleFieldChange('directory', event.target.value)
                          }
                          required
                        />
                      </Grid>
                    </>
                  )}
                  {isApiCategory && (
                    <>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label="API endpoint URL"
                          value={formValues.api_endpoint}
                          onChange={(event) =>
                            handleFieldChange(
                              'api_endpoint',
                              event.target.value
                            )
                          }
                          required
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          type="password"
                          label="API key"
                          value={formValues.api_key}
                          onChange={(event) =>
                            handleFieldChange('api_key', event.target.value)
                          }
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          multiline
                          minRows={2}
                          label="Headers (JSON)"
                          value={formValues.json_headers_text}
                          onChange={(event) =>
                            handleFieldChange(
                              'json_headers_text',
                              event.target.value
                            )
                          }
                          helperText="JSON object for the request headers."
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          multiline
                          minRows={3}
                          label="Body template (JSON)"
                          value={formValues.json_body_template_text}
                          onChange={(event) =>
                            handleFieldChange(
                              'json_body_template_text',
                              event.target.value
                            )
                          }
                          helperText="JSON payload template for the request body."
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Pagination parameter"
                          value={formValues.pagination_param}
                          onChange={(event) =>
                            handleFieldChange(
                              'pagination_param',
                              event.target.value
                            )
                          }
                          helperText="Optional query parameter to advance pages."
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Page size parameter"
                          value={formValues.page_size_param}
                          onChange={(event) =>
                            handleFieldChange(
                              'page_size_param',
                              event.target.value
                            )
                          }
                          helperText="Optional parameter to control page size."
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Initial page"
                          type="number"
                          value={formValues.initial_page}
                          onChange={(event) =>
                            handleFieldChange(
                              'initial_page',
                              event.target.value
                            )
                          }
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <Divider sx={{ my: 1 }} />
                        <Typography variant="subtitle1" sx={{ mb: 1 }}>
                          Auth (optional)
                        </Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label="Auth URL"
                          value={formValues.auth_url}
                          onChange={(event) =>
                            handleFieldChange('auth_url', event.target.value)
                          }
                          helperText="Call this first to retrieve a token."
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          select
                          fullWidth
                          label="Auth method"
                          value={formValues.auth_method}
                          onChange={(event) =>
                            handleFieldChange('auth_method', event.target.value)
                          }
                        >
                          <MenuItem value="POST">POST</MenuItem>
                          <MenuItem value="GET">GET</MenuItem>
                        </TextField>
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          multiline
                          minRows={2}
                          label="Auth headers (JSON)"
                          value={formValues.auth_headers_text}
                          onChange={(event) =>
                            handleFieldChange(
                              'auth_headers_text',
                              event.target.value
                            )
                          }
                          helperText='JSON headers for the auth call (e.g., {"Content-Type":"application/json"}).'
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          multiline
                          minRows={3}
                          label="Auth body (JSON)"
                          value={formValues.auth_body_text}
                          onChange={(event) =>
                            handleFieldChange(
                              'auth_body_text',
                              event.target.value
                            )
                          }
                          helperText="JSON payload for the auth request."
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Token path"
                          value={formValues.auth_token_path}
                          onChange={(event) =>
                            handleFieldChange(
                              'auth_token_path',
                              event.target.value
                            )
                          }
                          helperText="Dot path to the token in the auth JSON response (default: token)."
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Header name for token"
                          value={formValues.auth_header_name}
                          onChange={(event) =>
                            handleFieldChange(
                              'auth_header_name',
                              event.target.value
                            )
                          }
                          helperText="e.g., Authorization (leave blank to skip header injection)."
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Token prefix"
                          value={formValues.auth_token_prefix}
                          onChange={(event) =>
                            handleFieldChange(
                              'auth_token_prefix',
                              event.target.value
                            )
                          }
                          helperText='Prepended to the token (e.g., "Bearer ").'
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Query param for token"
                          value={formValues.auth_query_param}
                          onChange={(event) =>
                            handleFieldChange(
                              'auth_query_param',
                              event.target.value
                            )
                          }
                          helperText="If set, token is added as this query parameter instead of a header."
                        />
                      </Grid>
                    </>
                  )}
                  {(isEmailCategory || isSftpCategory) && (
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Filename substring filter"
                        value={formValues.filename_substring}
                        onChange={(event) =>
                          handleFieldChange(
                            'filename_substring',
                            event.target.value
                          )
                        }
                        helperText="Only process files whose filename contains this text."
                      />
                    </Grid>
                  )}
                </Grid>
              </CardContent>
            </Card>

            <Card>
              <CardHeader
                title="Column mappings"
                subheader="Map CSV headers to backend fields. Upload a sample CSV to help detect headers."
                action={
                  <Button
                    variant="outlined"
                    component="label"
                    startIcon={<UploadFileIcon />}
                  >
                    Upload sample
                    <input
                      type="file"
                      accept=".csv,text/csv"
                      hidden
                      onChange={handleCsvUpload}
                    />
                  </Button>
                }
              />
              <Divider />
              <CardContent>
                {!!headerSuggestions.length && (
                  <Stack direction="row" spacing={1} flexWrap="wrap" mb={2}>
                    {headerSuggestions.map((header) => (
                      <Chip key={header} label={header} variant="outlined" />
                    ))}
                  </Stack>
                )}
                <Stack spacing={2}>
                  {columnRows.map((row) => (
                    <Stack
                      key={row.id}
                      direction={{ xs: 'column', sm: 'row' }}
                      spacing={2}
                      alignItems="flex-end"
                    >
                      <TextField
                        label="CSV header"
                        fullWidth
                        value={row.header}
                        onChange={(event) =>
                          handleColumnChange(row.id, 'header', event.target.value)
                        }
                        helperText="Match the exact column title from the file."
                      />
                      <TextField
                        select
                        label="Backend field"
                        fullWidth
                        value={row.target}
                        onChange={(event) =>
                          handleColumnChange(row.id, 'target', event.target.value)
                        }
                      >
                        {(metadata.backend_fields || [])
                          .filter(
                            (field) =>
                              field === row.target ||
                              !usedColumnTargets.has(field)
                          )
                          .map((field) => (
                            <MenuItem key={field} value={field}>
                              {field}
                            </MenuItem>
                          ))}
                      </TextField>
                      <IconButton
                        color="error"
                        onClick={() =>
                          setColumnRows((prev) =>
                            prev.length > 1
                              ? prev.filter((item) => item.id !== row.id)
                              : prev
                          )
                        }
                        disabled={columnRows.length === 1}
                      >
                        <DeleteOutlineIcon />
                      </IconButton>
                    </Stack>
                  ))}
                </Stack>
                <Button
                  sx={{ mt: 2 }}
                  startIcon={<AddCircleOutlineIcon />}
                  onClick={() =>
                    setColumnRows((prev) => [...prev, createColumnRow()])
                  }
                >
                  Add mapping
                </Button>
              </CardContent>
            </Card>

            {!isApiCategory && (
              <Card>
                <CardHeader
                  title="Row filters"
                  subheader="Filter incoming rows before processing or transformations."
                />
                <Divider />
                <CardContent>
                  <Stack spacing={2}>
                    {filterRows.map((row) => {
                      const operator = FILTER_OPERATORS.find(
                        (item) => item.value === row.operator
                      );
                      return (
                        <Stack
                          key={row.id}
                          direction={{ xs: 'column', sm: 'row' }}
                          spacing={2}
                          alignItems="flex-end"
                        >
                          <TextField
                            label="CSV field"
                            fullWidth
                            value={row.field}
                            onChange={(event) =>
                              handleFilterChange(
                                row.id,
                                'field',
                                event.target.value
                              )
                            }
                          />
                          <TextField
                            select
                            label="Operator"
                            value={row.operator}
                            onChange={(event) =>
                              handleFilterChange(
                                row.id,
                                'operator',
                                event.target.value
                              )
                            }
                            sx={{ minWidth: { sm: 200 } }}
                          >
                            {FILTER_OPERATORS.map((option) => (
                              <MenuItem key={option.value} value={option.value}>
                                {option.label}
                              </MenuItem>
                            ))}
                          </TextField>
                          {operator?.needsValue && (
                            <TextField
                              label="Value(s)"
                              fullWidth
                              value={row.value}
                              onChange={(event) =>
                                handleFilterChange(
                                  row.id,
                                  'value',
                                  event.target.value
                                )
                              }
                              helperText={
                                row.operator === 'eq'
                                  ? 'Exact match'
                                  : 'Comma separated'
                              }
                            />
                          )}
                          <IconButton
                            color="error"
                            onClick={() => removeFilterRow(row.id)}
                            disabled={filterRows.length === 1}
                          >
                            <DeleteOutlineIcon />
                          </IconButton>
                        </Stack>
                      );
                    })}
                  </Stack>
                  <Button
                    sx={{ mt: 2 }}
                    startIcon={<AddCircleOutlineIcon />}
                    onClick={addFilterRow}
                  >
                    Add row filter
                  </Button>
                </CardContent>
              </Card>
            )}

            <Card>
              <CardHeader
                title="Transform rules"
                subheader="Add conditional logic to populate fields after the initial mapping."
              />
              <Divider />
              <CardContent>
                <Stack spacing={3}>
                  {transformRows.map((row) => (
                    <Box
                      key={row.id}
                      sx={{
                        border: (theme) => `1px solid ${theme.palette.divider}`,
                        borderRadius: 2,
                        p: 2,
                      }}
                    >
                      <Stack
                        direction={{ xs: 'column', sm: 'row' }}
                        spacing={2}
                        alignItems={{ sm: 'center' }}
                        mb={2}
                      >
                        <TextField
                          label="Set backend field"
                          fullWidth
                          value={row.setField}
                          onChange={(event) =>
                            handleTransformChange(
                              row.id,
                              'setField',
                              event.target.value
                            )
                          }
                        />
                        <TextField
                          select
                          label="Value source"
                          value={row.valueSource}
                          onChange={(event) =>
                            handleTransformChange(
                              row.id,
                              'valueSource',
                              event.target.value
                            )
                          }
                        >
                          <MenuItem value="field">Copy from CSV field</MenuItem>
                          <MenuItem value="constant">Use constant value</MenuItem>
                        </TextField>
                        <IconButton
                          color="error"
                          onClick={() =>
                            setTransformRows((prev) =>
                              prev.length > 1
                                ? prev.filter((item) => item.id !== row.id)
                                : prev
                            )
                          }
                          disabled={transformRows.length === 1}
                        >
                          <DeleteOutlineIcon />
                        </IconButton>
                      </Stack>

                      {row.valueSource === 'field' ? (
                        <Stack spacing={2} mb={2}>
                          <TextField
                            label="CSV field"
                            value={row.fieldName}
                            onChange={(event) =>
                              handleTransformChange(
                                row.id,
                                'fieldName',
                                event.target.value
                              )
                            }
                          />
                          <TextField
                            label="Default value"
                            value={row.defaultValue}
                            onChange={(event) =>
                              handleTransformChange(
                                row.id,
                                'defaultValue',
                                event.target.value
                              )
                            }
                            helperText="Optional fallback if no match is found."
                          />
                          <Stack spacing={1}>
                            {(row.valueMap || []).map((entry) => (
                              <Stack
                                key={entry.id}
                                direction={{ xs: 'column', sm: 'row' }}
                                spacing={1}
                                alignItems="flex-end"
                              >
                                <TextField
                                  label="If CSV value is"
                                  value={entry.match}
                                  onChange={(event) =>
                                    handleValueMapChange(
                                      row.id,
                                      entry.id,
                                      'match',
                                      event.target.value
                                    )
                                  }
                                />
                                <TextField
                                  label="Set to"
                                  value={entry.result}
                                  onChange={(event) =>
                                    handleValueMapChange(
                                      row.id,
                                      entry.id,
                                      'result',
                                      event.target.value
                                    )
                                  }
                                />
                                <IconButton
                                  color="error"
                                  onClick={() =>
                                    removeValueMap(row.id, entry.id)
                                  }
                                >
                                  <DeleteOutlineIcon />
                                </IconButton>
                              </Stack>
                            ))}
                            <Button
                              startIcon={<AddCircleOutlineIcon />}
                              onClick={() => addValueMap(row.id)}
                              variant="outlined"
                              size="small"
                              sx={{ alignSelf: 'flex-start' }}
                            >
                              Add value mapping
                            </Button>
                          </Stack>
                        </Stack>
                      ) : (
                        <TextField
                          label="Constant value"
                          value={row.constantValue}
                          onChange={(event) =>
                            handleTransformChange(
                              row.id,
                              'constantValue',
                              event.target.value
                            )
                          }
                          fullWidth
                          sx={{ mb: 2 }}
                        />
                      )}

                      <Divider sx={{ my: 2 }} />
                      <Typography variant="subtitle2" gutterBottom>
                        Conditions
                      </Typography>
                      <Stack spacing={2}>
                        {(row.conditions || []).map((condition) => {
                          const operator = CONDITION_OPERATORS.find(
                            (item) => item.value === condition.operator
                          );
                          return (
                            <Stack
                              key={condition.id}
                              direction={{ xs: 'column', sm: 'row' }}
                              spacing={2}
                              alignItems="flex-end"
                            >
                              <TextField
                                label="CSV field"
                                value={condition.field}
                                onChange={(event) =>
                                  handleConditionChange(
                                    row.id,
                                    condition.id,
                                    'field',
                                    event.target.value
                                  )
                                }
                              />
                              <TextField
                                select
                                label="Operator"
                                value={condition.operator}
                                onChange={(event) =>
                                  handleConditionChange(
                                    row.id,
                                    condition.id,
                                    'operator',
                                    event.target.value
                                  )
                                }
                              >
                                {CONDITION_OPERATORS.map((item) => (
                                  <MenuItem key={item.value} value={item.value}>
                                    {item.label}
                                  </MenuItem>
                                ))}
                              </TextField>
                              {operator?.needsValue && (
                                <TextField
                                  label="Value"
                                  value={condition.value}
                                  onChange={(event) =>
                                    handleConditionChange(
                                      row.id,
                                      condition.id,
                                      'value',
                                      event.target.value
                                    )
                                  }
                                />
                              )}
                              <IconButton
                                color="error"
                                onClick={() =>
                                  removeCondition(row.id, condition.id)
                                }
                              >
                                <DeleteOutlineIcon />
                              </IconButton>
                            </Stack>
                          );
                        })}
                        <Button
                          startIcon={<AddCircleOutlineIcon />}
                          onClick={() => addCondition(row.id)}
                          variant="outlined"
                          size="small"
                          sx={{ alignSelf: 'flex-start' }}
                        >
                          Add condition
                        </Button>
                      </Stack>
                    </Box>
                  ))}
                </Stack>
                <Button
                  sx={{ mt: 2 }}
                  startIcon={<AddCircleOutlineIcon />}
                  onClick={() =>
                    setTransformRows((prev) => [...prev, createTransformRow()])
                  }
                >
                  Add transformation
                </Button>
              </CardContent>
            </Card>

            <Stack direction="row" spacing={2} justifyContent="flex-end">
              <Button
                variant="outlined"
                onClick={handleRunNow}
                disabled={!activeId || runLoading}
              >
                {runLoading ? 'Queueing…' : 'Run now'}
              </Button>
              <Button variant="outlined" onClick={resetForm}>
                Reset
              </Button>
              <Button
                variant="outlined"
                onClick={duplicateMapping}
                disabled={!activeId}
              >
                Duplicate
              </Button>
              <Button
                variant="contained"
                onClick={handleSave}
                disabled={saving}
              >
                {activeId ? 'Update mapping' : 'Create mapping'}
              </Button>
            </Stack>
          </Stack>
        </Grid>
      </Grid>
    </Box>
  );
}
